import { useState, useEffect, useRef } from "react";
import "./Btn.css";
import { F, btnSizes, btnBase } from "../theme/tokens";

/* ───── design tokens (no CSS vars needed) ───── */
const T = {
  ink: "#061427",
  inkSoft: "#2F4361",
  inkMid: "#214F73",
  inkMute: "#5D6D84",
  line: "#e3eef5",
  accent: "#009CDD",
  accent2: "#59C9F4",
  accentTint: "#EAF8FF",
  border: "#def0f9",
  textTitle: "#182E4F",
  textNum: "#5D6D84",
  fontBody: F.body,
  ease: "cubic-bezier(.22, 1, .36, 1)",
  shadow: "0 2px 12px rgba(24,46,79,0.06)",
};

function useMagnet(strength = 6) {
  const ref = useRef(null);
  const [t, setT] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const move = (e) => {
      const r = el.getBoundingClientRect();
      const cx = r.left + r.width / 2;
      const cy = r.top + r.height / 2;
      const dx = (e.clientX - cx) / r.width;
      const dy = (e.clientY - cy) / r.height;
      setT({ x: dx * strength, y: dy * strength });
    };
    const leave = () => setT({ x: 0, y: 0 });
    el.addEventListener("mousemove", move);
    el.addEventListener("mouseleave", leave);
    return () => {
      el.removeEventListener("mousemove", move);
      el.removeEventListener("mouseleave", leave);
    };
  }, [strength]);

  return [ref, t];
}

const ArrowRight = ({ size = 16 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M5 12h14M12 5l7 7-7 7" />
  </svg>
);

/** Stili hover allineati al prototipo (gradient sx→dx). */
function getVariantStyle(variant, h) {
  const map = {
    /* Hero CTA — hover: azzurro chiaro → bianco, testo accent */
    primaryLight: {
      background: h
        ? "linear-gradient(90deg, #B7DAE9 0%, #EAF8FF 48%, #ffffff 100%)"
        : T.accent,
      color: h ? T.ink : "#fff",
      boxShadow: h
        ? "0 14px 42px rgba(255,255,255,0.28), 0 4px 18px rgba(89,201,244,0.18)"
        : "0 8px 28px rgba(0,156,221,0.32)",
    },
    /* Outline su dark — hover: bagliore cyan a sinistra che sfuma */
    ghostLight: {
      background: h
        ? "linear-gradient(90deg, rgba(89,201,244,0.62) 0%, rgba(89,201,244,0.2) 34%, rgba(6,20,39,0.55) 68%, rgba(6,20,39,0.38) 100%)"
        : "rgba(255,255,255,0.04)",
      color: "#fff",
      border: `1px solid ${h ? "rgba(255,255,255,0.42)" : "rgba(255,255,255,0.18)"}`,
      backdropFilter: "blur(12px)",
      WebkitBackdropFilter: "blur(12px)",
      boxShadow: h ? "0 10px 32px rgba(89,201,244,0.14)" : "none",
    },
    /* CTA pieno — default accent, hover gradient scuro */
    primary: {
      background: h
        ? `linear-gradient(90deg, #3d5f82 0%, ${T.inkSoft} 42%, ${T.ink} 100%)`
        : T.accent,
      color: "#fff",
      boxShadow: h
        ? "0 16px 44px rgba(6,20,39,0.38)"
        : "0 8px 24px rgba(0,156,221,0.28)",
    },
    pill: {
      background: h
        ? `linear-gradient(90deg, #4DBAE7 0%, ${T.inkSoft} 38%, ${T.ink} 100%)`
        : `linear-gradient(90deg, ${T.inkMid} 0%, ${T.inkSoft} 52%, ${T.ink} 100%)`,
      color: "#fff",
      borderRadius: 999,
      boxShadow: h
        ? "0 16px 44px rgba(6,20,39,0.38)"
        : "0 10px 30px rgba(6,20,39,0.28)",
    },
    /* CTA finale — hover: tint → bianco, testo accent */
    hero: {
      background: h
        ? `linear-gradient(90deg, ${T.accentTint} 0%, #f4fbff 52%, #ffffff 100%)`
        : T.accent,
      color: h ? T.accent : "#fff",
      borderRadius: 14,
      letterSpacing: "0.06em",
      boxShadow: h
        ? "0 14px 40px rgba(255,255,255,0.35), 0 4px 16px rgba(89,201,244,0.2)"
        : "0 8px 28px rgba(0,156,221,0.28)",
    },
    ghost: {
      background: h ? T.accentTint : "transparent",
      color: h ? T.accent : T.ink,
      border: `1px solid ${h ? T.accent : T.line}`,
    },
    white: {
      background: h
        ? `linear-gradient(90deg, ${T.accentTint} 0%, #ffffff 100%)`
        : "#fff",
      color: h ? T.accent : T.textTitle,
      boxShadow: T.shadow,
    },
    blue: {
      background: h
        ? `linear-gradient(90deg, ${T.accent2} 0%, ${T.accent} 100%)`
        : T.accent,
      color: "#fff",
      borderRadius: 50,
      boxShadow: h ? "0 12px 36px rgba(0,156,221,0.35)" : "0 6px 20px rgba(0,156,221,0.22)",
    },
    blueOutline: {
      background: h ? T.accentTint : "transparent",
      color: T.accent,
      border: `1.5px solid ${T.accent}`,
      borderRadius: 50,
    },
    outline: {
      background: h ? T.accentTint : "transparent",
      color: h ? T.accent : T.textNum,
      border: `1.5px solid ${h ? T.accent : T.border}`,
    },
  };
  return map[variant] || map.primary;
}

export default function Btn({
  children,
  variant = "primary",
  size = "md",
  href,
  onClick,
  iconRight = true,
  style: styleProp = {},
  type = "button",
}) {
  const [h, setH] = useState(false);
  const [ref, m] = useMagnet(6);

  const sizes = btnSizes[size] || btnSizes.md;

  const {
    background: _bg,
    color: _color,
    boxShadow: _shadow,
    border: _border,
    transform: _transform,
    transition: _transition,
    textTransform: _textTransform,
    overflow: _overflow,
    position: _position,
    ...layoutStyle
  } = styleProp;

  const sharedStyle = {
    display: "inline-flex",
    alignItems: "center",
    gap: 10,
    height: sizes.height,
    padding: sizes.padding,
    fontFamily: btnBase.fontFamily,
    fontSize: sizes.fontSize,
    fontWeight: btnBase.fontWeight,
    letterSpacing: btnBase.letterSpacing,
    textDecoration: "none",
    cursor: "pointer",
    whiteSpace: "nowrap",
    borderRadius: btnBase.borderRadius,
    border: "1px solid transparent",
    willChange: "transform",
    position: "relative",
    overflow: "hidden",
    ...layoutStyle,
    ...getVariantStyle(variant, h),
    textTransform: "uppercase",
    transform: `translate3d(${m.x}px, ${m.y}px, 0)`,
  };

  const setHover = (on) => () => setH(on);
  const commonProps = {
    ref,
    "data-ob-btn": true,
    "data-ob-variant": variant,
    onClick,
    onMouseEnter: setHover(true),
    onMouseLeave: setHover(false),
    onFocus: setHover(true),
    onBlur: setHover(false),
    style: sharedStyle,
  };

  const inner = (
    <>
      <span style={{ position: "relative", zIndex: 1 }}>{children}</span>
      {iconRight && (
        <span
          data-ob-arrow
          style={{
            display: "inline-flex",
            position: "relative",
            zIndex: 1,
            transform: h ? "translateX(4px)" : "translateX(0)",
          }}
        >
          <ArrowRight />
        </span>
      )}
    </>
  );

  if (href) {
    return (
      <a href={href} {...commonProps}>
        {inner}
      </a>
    );
  }

  return (
    <button type={type} {...commonProps}>
      {inner}
    </button>
  );
}
